import { LEVEL_LABELS, type JurisdictionLevel } from "@/lib/jurisdiction";
import type { RetrievalDocument } from "@/lib/retrieval";

/**
 * The one-paragraph "in brief" lede for a finished lookup — a deterministic
 * sentence per observed layer status, so a stack that is mostly pointers
 * LEADS with what was found and where the real rules live instead of
 * burying the reader in fragments (PV-17).
 *
 * Copy discipline: every clause maps 1:1 to document statuses the backend
 * actually returned; nothing here is inferred or generated. The only legal
 * claim it relays is the curated scope note's, and it relays it BY
 * REFERENCE ("the federal layer carries a note") rather than asserting it
 * in the summary's own voice — the note is the product's single unsourced
 * statement type, and this stays a status readout.
 */
export function composeLookupSummary(
  layers: { level: string; documents: RetrievalDocument[] }[],
): string | null {
  const label = (level: string) =>
    (LEVEL_LABELS[level as JurisdictionLevel] ?? level).toLowerCase();

  const dedupeLabels = (subset: { level: string }[]) => {
    const seen: string[] = [];
    for (const layer of subset) {
      const text = label(layer.level);
      if (!seen.includes(text)) seen.push(text);
    }
    return seen;
  };

  const listOf = (items: string[]) => {
    if (items.length <= 1) return items[0] ?? "";
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
  };

  const has = (layer: { documents: RetrievalDocument[] }, status: string) =>
    layer.documents.some((d) => d.status === status);

  const okCount = layers.reduce(
    (n, l) => n + l.documents.filter((d) => d.status === "ok").length,
    0,
  );
  const okLevels = dedupeLabels(layers.filter((l) => has(l, "ok")));
  const hasScopeNote = layers.some((l) => has(l, "not_applicable"));
  const noResultLevels = dedupeLabels(
    layers.filter((l) => has(l, "no_results") && !has(l, "ok")),
  );
  const pointerLevels = dedupeLabels(
    layers.filter((l) => has(l, "not_yet_supported") && !has(l, "ok")),
  );
  const unavailableCount = layers.reduce(
    (n, l) => n + l.documents.filter((d) => d.status === "unavailable").length,
    0,
  );

  const parts: string[] = [];
  if (okCount > 0) {
    parts.push(
      `${okCount === 1 ? "One document" : `${okCount} documents`} retrieved ` +
        `from official sources at the ${listOf(okLevels)} ` +
        `${okLevels.length > 1 ? "levels" : "level"}, each quoted below with ` +
        `its citation.`,
    );
  }
  if (hasScopeNote) {
    parts.push(
      "The federal layer carries a note for this topic: the governing rules " +
        "are set locally, with the narrow federal exceptions named there.",
    );
  }
  if (noResultLevels.length > 0) {
    parts.push(
      `The ${listOf(noResultLevels)} ` +
        `${noResultLevels.length > 1 ? "searches" : "search"} ran and ` +
        `matched nothing for this topic.`,
    );
  }
  if (pointerLevels.length > 0) {
    parts.push(
      `Prosa cannot retrieve the ${listOf(pointerLevels)} ` +
        `${pointerLevels.length > 1 ? "layers" : "layer"} yet — each links ` +
        `to its official source.`,
    );
  }
  if (unavailableCount > 0) {
    parts.push(
      `${unavailableCount === 1 ? "One source" : `${unavailableCount} sources`} ` +
        `did not answer; nothing is shown rather than the wrong text.`,
    );
  }

  return parts.length > 0 ? parts.join(" ") : null;
}
